import { canonicalSupplier } from '@theme/fastutv-search-intent';

const vendorSelector = 'input[type="checkbox"][name="filter.p.vendor"]';
const enhancedLists = new WeakMap();
let proxySequence = 0;

function supplierLabel(input, row) {
  const label = row.querySelector('label');
  return input.getAttribute('aria-label') || input.dataset.label ||
    label?.querySelector('.checkbox__label-text')?.textContent?.trim() ||
    label?.textContent?.trim() || input.value;
}

function countFor(input, row) {
  const node = row.querySelector('[data-count], .facets__count, .checkbox__count, .facets__label-count');
  const value = input.dataset.count ?? node?.dataset.count ?? node?.textContent;
  if (value == null || value.trim() === '') return null;
  const number = Number(value.replace(/[(),\s]/g, ''));
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function updateSummary(list, groups) {
  const status = list.closest('details')?.querySelector('facet-status-component[facet-type="list"]');
  const summary = status?.querySelector('[ref="facetStatus"]');
  if (!summary) return;
  const active = groups.filter((group) => group.inputs.some((input) => input.checked));
  const horizontal = status.dataset.filterStyle === 'horizontal';
  const text = active.length === 0 ? '' : horizontal && active.length === 1 ? active[0].name : String(active.length);
  if (summary.textContent !== text) summary.textContent = text;
  const bubble = active.length > (horizontal ? 1 : 0);
  summary.classList.toggle('bubble', bubble);
  summary.classList.toggle('facets__bubble', bubble);
}

function syncProxies(list, state) {
  for (const group of state.groups) {
    group.proxy.checked = group.inputs.some((input) => input.checked);
    group.proxy.disabled = group.inputs.every((input) => input.disabled) && !group.proxy.checked;
    group.row.classList.toggle('facets__inputs-list-item--disabled', group.proxy.disabled);
    group.row.querySelector('.checkbox')?.classList.toggle('checkbox--disabled', group.proxy.disabled);
  }
  updateSummary(list, state.groups);
}

function enhanceList(list, inputs) {
  const current = enhancedLists.get(list);
  if (current && current.inputs.length === inputs.length &&
      current.inputs.every((input, index) => input === inputs[index]) &&
      current.groups.every((group) => group.row.parentElement === list)) {
    syncProxies(list, current);
    return;
  }
  current?.controller.abort();
  list.querySelectorAll('[data-fastutv-supplier-choice]').forEach((row) => row.remove());

  const groupsByName = new Map();
  for (const input of inputs) {
    const row = input.closest('li');
    if (!row || row.parentElement !== list) continue;
    const label = supplierLabel(input, row);
    const name = canonicalSupplier(label) || label;
    let group = groupsByName.get(name);
    if (!group) {
      group = { name, inputs: [], rawRows: [], counts: [] };
      groupsByName.set(name, group);
    }
    group.inputs.push(input);
    group.rawRows.push(row);
    group.counts.push(countFor(input, row));
  }
  const groups = [...groupsByName.values()].sort((a, b) =>
    a.name.localeCompare(b.name, document.documentElement.lang || undefined, { sensitivity: 'base', numeric: true }));

  for (const group of groups) {
    const row = group.rawRows[0].cloneNode(true);
    row.querySelectorAll('[id], [ref], [on\\:pointerenter], [on\\:pointerleave], [on\\:keydown]').forEach((node) => {
      node.removeAttribute('id');
      node.removeAttribute('ref');
      node.removeAttribute('on:pointerenter');
      node.removeAttribute('on:pointerleave');
      node.removeAttribute('on:keydown');
    });
    row.removeAttribute('id');
    row.removeAttribute('ref');
    row.removeAttribute('hidden');
    row.removeAttribute('aria-hidden');
    row.removeAttribute('style');
    row.removeAttribute('data-fastutv-supplier-raw');
    row.removeAttribute('data-skip-node-update');
    row.classList.remove('hidden', 'mobile:hidden');
    row.dataset.fastutvSupplierChoice = group.name;
    const proxy = row.querySelector('input');
    if (!proxy) continue;
    proxy.removeAttribute('name');
    proxy.removeAttribute('form');
    proxy.removeAttribute('ref');
    proxy.removeAttribute('tabindex');
    proxy.id = `FastUTVSupplier-${++proxySequence}`;
    proxy.value = group.name;
    proxy.dataset.label = group.name;
    proxy.setAttribute('aria-label', group.name);
    const label = row.querySelector('label');
    if (label) {
      label.htmlFor = proxy.id;
      label.removeAttribute('tabindex');
      const text = label.querySelector('.checkbox__label-text');
      if (text) text.textContent = group.name;
      else label.textContent = group.name;
    }
    const countNode = row.querySelector('[data-count], .facets__count, .checkbox__count, .facets__label-count');
    if (countNode && group.counts.every((count) => count !== null)) {
      const count = group.counts.reduce((sum, item) => sum + item, 0);
      countNode.textContent = countNode.textContent.includes('(') ? `(${count})` : String(count);
      if (countNode.hasAttribute('data-count')) countNode.dataset.count = String(count);
    }
    group.proxy = proxy;
    group.row = row;
    for (const rawRow of group.rawRows) {
      rawRow.hidden = true;
      rawRow.setAttribute('aria-hidden', 'true');
      rawRow.dataset.fastutvSupplierRaw = '';
      rawRow.style.display = 'none';
    }
    list.append(row);
  }
  const validGroups = groups.filter((group) => group.proxy);
  const state = { inputs, groups: validGroups, controller: new AbortController() };
  enhancedLists.set(list, state);
  list.dataset.fastutvSupplierFacets = '';
  list.style.maxHeight = 'min(50vh, 26rem)';
  list.style.overflowY = 'auto';
  const showMore = list.closest('show-more-component');
  if (showMore) {
    const button = showMore.querySelector('[ref="showMoreButton"]');
    if (button) {
      button.hidden = true;
      button.style.display = 'none';
    }
  }
  // Native facet handlers serialize the original inputs on the bubbling change.
  // Proxies deliberately have no name and never alter initial URL selections.
  list.addEventListener('change', (event) => {
    const group = validGroups.find((item) => item.proxy === event.target);
    if (group) {
      for (const input of group.inputs) input.checked = group.proxy.checked;
    }
    queueMicrotask(() => {
      if (list.isConnected) syncProxies(list, state);
    });
  }, { capture: true, signal: state.controller.signal });
  syncProxies(list, state);
}

/**
 * Normalize each native supplier list independently after initial/section render.
 * Original named inputs remain available to native Shopify facet serialization.
 * @param {Document|Element} [root=document]
 */
export function enhanceSupplierFacets(root = document) {
  const lists = new Map();
  const inputs = [...root.querySelectorAll(vendorSelector)];
  if (root.matches?.(vendorSelector)) inputs.unshift(root);
  for (const input of inputs) {
    const list = input.closest('ul');
    if (!list) continue;
    const group = lists.get(list) || [];
    group.push(input);
    lists.set(list, group);
  }
  for (const [list, listInputs] of lists) enhanceList(list, listInputs);
}
